import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { calculatePayroll, centsToEur, type PayrollInput } from './payroll-engine'
import type { TaxClass } from '../../config/rts'
import styles from './SimulatorPage.module.css'

export function SimulatorPage() {
  const { t } = useTranslation()

  const [grossEur, setGrossEur] = useState('4000')
  const [taxClass, setTaxClass] = useState<TaxClass>('1')
  const [overtimeHours, setOvertimeHours] = useState('0')
  const [thirteenthMonthEur, setThirteenthMonthEur] = useState('0')
  const [mealVouchers, setMealVouchers] = useState('0')
  const [mealVoucherValue, setMealVoucherValue] = useState('10.80')
  const [carBenefitEur, setCarBenefitEur] = useState('0')
  const [housingBenefitEur, setHousingBenefitEur] = useState('0')

  const input: PayrollInput = useMemo(() => ({
    grossBaseCents: Math.round(parseFloat(grossEur || '0') * 100),
    taxClass,
    overtimeHours: parseFloat(overtimeHours || '0'),
    thirteenthMonthCents: Math.round(parseFloat(thirteenthMonthEur || '0') * 100),
    mealVouchersCount: parseInt(mealVouchers || '0', 10),
    mealVoucherValueCents: Math.round(parseFloat(mealVoucherValue || '0') * 100),
    carBenefitCents: Math.round(parseFloat(carBenefitEur || '0') * 100),
    housingBenefitCents: Math.round(parseFloat(housingBenefitEur || '0') * 100),
  }), [grossEur, taxClass, overtimeHours, thirteenthMonthEur, mealVouchers, mealVoucherValue, carBenefitEur, housingBenefitEur])

  const r = useMemo(() => calculatePayroll(input), [input])

  return (
    <div className={styles.page}>
      <h1>{t('simulator.title')}</h1>
      <p className={styles.subtitle}>{t('simulator.subtitle')}</p>

      <div className={styles.grid}>
        {/* Inputs */}
        <div className={styles.inputPanel}>
          <h3 className={styles.sectionTitle}>{t('simulator.section_salary')}</h3>

          <div className={styles.field}>
            <label>{t('simulator.gross_salary')}</label>
            <div className={styles.inputWithUnit}>
              <input type="number" value={grossEur} onChange={e => setGrossEur(e.target.value)} min="0" step="100" />
              <span>EUR</span>
            </div>
          </div>

          <div className={styles.field}>
            <label>{t('simulator.tax_class')}</label>
            <select value={taxClass} onChange={e => setTaxClass(e.target.value as TaxClass)}>
              <option value="1">{t('simulator.class_1')}</option>
              <option value="1a">{t('simulator.class_1a')}</option>
              <option value="2">{t('simulator.class_2')}</option>
            </select>
          </div>

          <div className={styles.field}>
            <label>{t('simulator.overtime_hours')}</label>
            <input type="number" value={overtimeHours} onChange={e => setOvertimeHours(e.target.value)} min="0" step="1" />
          </div>

          <div className={styles.field}>
            <label>{t('simulator.thirteenth_month')}</label>
            <div className={styles.inputWithUnit}>
              <input type="number" value={thirteenthMonthEur} onChange={e => setThirteenthMonthEur(e.target.value)} min="0" step="100" />
              <span>EUR</span>
            </div>
          </div>

          <h3 className={styles.sectionTitle}>{t('simulator.section_benefits')}</h3>

          <div className={styles.field}>
            <label>{t('simulator.car_benefit')}</label>
            <div className={styles.inputWithUnit}>
              <input type="number" value={carBenefitEur} onChange={e => setCarBenefitEur(e.target.value)} min="0" step="10" />
              <span>EUR</span>
            </div>
            <span className={styles.fieldHint}>{t('simulator.car_benefit_hint')}</span>
          </div>

          <div className={styles.field}>
            <label>{t('simulator.housing_benefit')}</label>
            <div className={styles.inputWithUnit}>
              <input type="number" value={housingBenefitEur} onChange={e => setHousingBenefitEur(e.target.value)} min="0" step="50" />
              <span>EUR</span>
            </div>
          </div>

          <div className={styles.field}>
            <label>{t('simulator.meal_vouchers')}</label>
            <div className={styles.fieldRow}>
              <input type="number" value={mealVouchers} onChange={e => setMealVouchers(e.target.value)} min="0" step="1" placeholder={t('simulator.meal_vouchers_count_ph')} />
              <div className={styles.inputWithUnit}>
                <input type="number" value={mealVoucherValue} onChange={e => setMealVoucherValue(e.target.value)} min="0" step="0.20" />
                <span>EUR</span>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className={styles.resultPanel}>
          <div className={styles.netBox}>
            <span className={styles.netLabel}>{t('simulator.a_verser')}</span>
            <span className={styles.netAmount}>{centsToEur(r.aVerserCents)} EUR</span>
          </div>

          <div className={styles.breakdown}>
            <Row label={t('simulator.gross_base')} value={r.grossBaseCents} />
            {r.overtimeCents > 0 && <Row label={t('simulator.overtime')} value={r.overtimeCents} indent />}
            {r.thirteenthMonthCents > 0 && <Row label={t('simulator.thirteenth')} value={r.thirteenthMonthCents} indent />}
            {r.benefitsInKindCents > 0 && <Row label={t('simulator.benefits_in_kind')} value={r.benefitsInKindCents} indent />}
            <Row label={t('simulator.gross_total')} value={r.grossTotalCents} bold />

            <Separator />

            <Row label={t('simulator.ss_employee')} value={-r.ssEmployeeTotalCents} negative />
            <Row label={t('simulator.ss_sickness')} value={-r.ssEmployeeMaladieCents} indent small />
            <Row label={t('simulator.ss_pension')} value={-r.ssEmployeePensionCents} indent small />
            <Row label={t('simulator.ss_dependency')} value={-r.ssEmployeeDependanceCents} indent small />
            {r.cappedGrossCents < r.grossTotalCents && <Row label={t('simulator.capped_notice')} value={r.cappedGrossCents} indent small />}

            <Separator />

            <Row label={t('simulator.abatements')} value={-r.totalAbatementsCents} />
            <Row label={t('simulator.taxable_income')} value={r.taxableIncomeCents} bold />
            <Row label={t('simulator.rts')} value={-r.rtsCents} negative />

            <Separator />

            <Row label={t('simulator.net')} value={r.netCents} bold />

            <Separator />

            <Row label={t('simulator.credits')} value={r.totalCreditsCents} positive />
            {r.benefitsInKindCents > 0 && <Row label={t('simulator.minus_benefits')} value={-r.benefitsInKindCents} indent small negative />}
            {r.mealVouchersEmployerCents > 0 && <Row label={t('simulator.meal_vouchers_employer')} value={r.mealVouchersEmployerCents} indent small positive />}
            <Row label={t('simulator.a_verser')} value={r.aVerserCents} bold />

            <Separator />

            <Row label={t('simulator.employer_cost')} value={r.totalCostCents} bold muted />
            <Row label={t('simulator.ss_employer')} value={r.ssEmployerTotalCents} indent small />
          </div>
        </div>
      </div>
    </div>
  )
}

function Row({ label, value, bold, negative, positive, indent, small, muted }: {
  label: string; value: number; bold?: boolean; negative?: boolean; positive?: boolean; indent?: boolean; small?: boolean; muted?: boolean
}) {
  const className = [styles.row, bold && styles.rowBold, indent && styles.rowIndent, small && styles.rowSmall, muted && styles.rowMuted].filter(Boolean).join(' ')
  const valueClass = [styles.rowValue, negative && styles.rowNegative, positive && styles.rowPositive].filter(Boolean).join(' ')
  return (
    <div className={className}>
      <span>{label}</span>
      <span className={valueClass}>{centsToEur(value)} EUR</span>
    </div>
  )
}

function Separator() {
  return <div className={styles.separator} />
}
